import { useAuth0 } from "@auth0/auth0-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import {
  promoService,
  PromoClaimRejection,
  PromoClaimResult,
  PromoClaimSuccess,
} from "../services/promo.service";
import { GENERIC_ERROR_MESSAGE } from "../utils/errorMessages";
import { getLogger } from "../utils/logger";

const logger = getLogger("promo-claim-page");

interface OutcomeCopy {
  title: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
}

/**
 * One friendly screen per refusal. A teacher who follows the link and can't
 * claim still gets a plain-English reason and somewhere to go next, so no
 * outcome ever leaves the page empty.
 */
const REJECTION_COPY: Record<PromoClaimRejection, OutcomeCopy> = {
  already_claimed: {
    title: "You've already claimed this one",
    body: "Your free fortnight has been applied to this account. If it's still running, everything is unlocked on your dashboard.",
    ctaLabel: "Go to my dashboard",
    ctaTo: "/dashboard",
  },
  active_subscription: {
    title: "You already have full access",
    body: "Your subscription is active, so there's nothing to claim — every tool is already open to you.",
    ctaLabel: "Go to my dashboard",
    ctaTo: "/dashboard",
  },
  trial_active: {
    title: "Your free trial is still running",
    body: "You already have full access while the trial lasts. Come back once it finishes and claim your two weeks then.",
    ctaLabel: "Go to my dashboard",
    ctaTo: "/dashboard",
  },
  not_eligible: {
    title: "This offer isn't available on your account",
    body: "The free fortnight is for teachers whose trial or subscription has ended. If you think that's you, get in touch and we'll sort it out.",
    ctaLabel: "See the plans",
    ctaTo: "/pricing",
  },
  promo_ended: {
    title: "This offer has closed",
    body: "The offer has finished up. You can still pick a plan whenever you're ready.",
    ctaLabel: "See the plans",
    ctaTo: "/pricing",
  },
};

const formatPromoEnd = (promoEndUtc: string | null): string | null => {
  if (!promoEndUtc) {
    return null;
  }

  const endDate = new Date(promoEndUtc);

  if (Number.isNaN(endDate.getTime())) {
    return null;
  }

  return endDate.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Landing page for the winback offer link. Signed-in only (the claim is tied
 * to the account), a single action, and a distinct outcome screen for each
 * answer the API can give.
 */
export const PromoClaimPage: React.FC = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState<PromoClaimResult | null>(null);
  const [tokenRefreshFailed, setTokenRefreshFailed] = useState(false);

  // The API decides access from the access token, so a written grant stays
  // invisible until Auth0 mints a new one. Any screen that sends the user to
  // an "unlocked" dashboard must wait for this refresh first.
  const refreshAccessToken = async () => {
    try {
      await getAccessTokenSilently({ cacheMode: "off" });
    } catch (refreshError) {
      logger.error("Could not refresh the access token after claiming", refreshError);
      setTokenRefreshFailed(true);
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    setTokenRefreshFailed(false);

    try {
      const accessToken = await getAccessTokenSilently();
      const claimResult = await promoService.claimFreeWeek(accessToken);

      if (claimResult.outcome === "success" || claimResult.outcome === "already_claimed") {
        // "Already claimed" needs the refresh as much as success does: it is
        // what a retry sees after a claim whose response was lost, and what a
        // second device sees — either way the session token predates the grant
        // while the screen promises an unlocked dashboard.
        await refreshAccessToken();
      }

      setResult(claimResult);
    } catch (error) {
      logger.error("Promo claim failed", error);
      setResult({ outcome: "error", message: GENERIC_ERROR_MESSAGE });
    } finally {
      setClaiming(false);
    }
  };

  const renderIdle = () => (
    <>
      <h1 className="page-header__title">Welcome back — two free weeks of full access are ready</h1>
      <p className="page-header__subtitle">
        Two weeks on the Pro plan, with no card and nothing to cancel.
      </p>
      <ul className="promo-claim__points">
        <li>Lesson plans and units of work</li>
        <li>Differentiated worksheets</li>
        <li>Report comments and parent correspondence</li>
        <li>Illustrated classroom slides</li>
      </ul>
      <button
        type="button"
        className="promo-claim__button"
        onClick={handleClaim}
        disabled={claiming}
      >
        {claiming ? "Claiming your two weeks…" : "Claim my two free weeks"}
      </button>
    </>
  );

  const renderSuccess = (success: PromoClaimSuccess) => {
    const endDate = formatPromoEnd(success.promoEndUtc);

    return (
      <>
        <h1 className="page-header__title">You're all set — enjoy your two weeks</h1>
        <p className="page-header__subtitle">
          {endDate
            ? `Full access is on your account until ${endDate}.`
            : "Full access is on your account for the next two weeks."}
        </p>
        <p className="promo-claim__note">
          Anything you generate is saved in My documents and stays there after the two weeks end.
        </p>
        {tokenRefreshFailed ? (
          <>
            <p className="promo-claim__note">
              Your free access is applied, but this session couldn&apos;t pick it up automatically.
            </p>
            {/* Token renewal runs on refresh tokens, but the grant can still
                fail — a revoked or expired refresh token, rotation reuse — so
                the recovery is a full sign-in redirect: it re-runs the login
                pipeline and mints fresh tokens from scratch. */}
            <button
              type="button"
              className="promo-claim__button"
              onClick={() => loginWithRedirect({ appState: { returnTo: "/dashboard" } })}
            >
              Sign in again to unlock it
            </button>
          </>
        ) : (
          /* A full page load rather than a client-side Link: the fresh token has
             just changed what this account can do, and reloading is what makes
             the whole app — the subscription claim it reads and the cached usage
             summary it fetches once per load — pick the new state up. */
          <a href="/dashboard" className="promo-claim__cta">
            Go to my dashboard
          </a>
        )}
      </>
    );
  };

  const renderError = (message: string) => (
    <>
      <h1 className="page-header__title">We couldn't claim your free access just yet</h1>
      <p className="promo-claim__error">{message}</p>
      <div className="promo-claim__actions">
        <button
          type="button"
          className="promo-claim__button"
          onClick={handleClaim}
          disabled={claiming}
        >
          {claiming ? "Trying again…" : "Try again"}
        </button>
        <Link to="/dashboard" className="promo-claim__cta promo-claim__cta--quiet">
          Go to my dashboard
        </Link>
      </div>
    </>
  );

  const renderRejection = (rejection: PromoClaimRejection) => {
    const copy = REJECTION_COPY[rejection];

    if (rejection === "already_claimed") {
      // This screen promises an unlocked dashboard, so it navigates the way the
      // success screen deliberately does: a full page load rather than a
      // client-side Link, so the whole app re-reads the freshly minted token.
      // When the refresh-token renewal failed (revoked or expired token), a
      // redirect sign-in is the only refresh left, so offer that instead of a
      // link to a still-locked dashboard.
      return (
        <>
          <h1 className="page-header__title">{copy.title}</h1>
          <p className="page-header__subtitle">{copy.body}</p>
          {tokenRefreshFailed ? (
            <button
              type="button"
              className="promo-claim__button"
              onClick={() => loginWithRedirect({ appState: { returnTo: copy.ctaTo } })}
            >
              Sign in again to unlock it
            </button>
          ) : (
            <a href={copy.ctaTo} className="promo-claim__cta">
              {copy.ctaLabel}
            </a>
          )}
        </>
      );
    }

    return (
      <>
        <h1 className="page-header__title">{copy.title}</h1>
        <p className="page-header__subtitle">{copy.body}</p>
        <Link to={copy.ctaTo} className="promo-claim__cta">
          {copy.ctaLabel}
        </Link>
      </>
    );
  };

  const renderOutcome = () => {
    if (!result) {
      return renderIdle();
    }

    if (result.outcome === "success") {
      return renderSuccess(result);
    }

    if (result.outcome === "error") {
      return renderError(result.message);
    }

    return renderRejection(result.outcome);
  };

  return (
    <PageLayout className="promo-claim-layout">
      <div className="page-container">
        <div className="promo-claim" role="status">
          {renderOutcome()}
        </div>
      </div>
    </PageLayout>
  );
};
